/* CrownLab site interactions: hero, tabs, rails, collection filters, product form, recommendations and 3D motion. */
const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
const isRtl = (el) => getComputedStyle(el).direction === 'rtl';

const once = (el, key) => {
  if (el.dataset[key]) return false;
  el.dataset[key] = 'true';
  return true;
};

/* Hero slideshow */
const initHero = (root) => {
  if (!once(root, 'clReady')) return;
  const slides = [...root.querySelectorAll('.cl-hero__slide')];
  const worn = [...root.querySelectorAll('.cl-hero__worn')];
  const dots = [...root.querySelectorAll('.cl-hero__progress button')];
  if (slides.length < 2) return;
  const delay = Number(root.dataset.delay || 7) * 1000;
  root.style.setProperty('--cl-delay', `${delay}ms`);
  let index = 0;
  let timer;
  const show = (next) => {
    index = (next + slides.length) % slides.length;
    slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
    worn.forEach((card) => (card.hidden = Number(card.dataset.index) !== index));
    dots.forEach((dot, i) => {
      dot.setAttribute('aria-current', 'false');
      if (i === index) requestAnimationFrame(() => dot.setAttribute('aria-current', 'true'));
    });
  };
  const stop = () => {
    clearTimeout(timer);
    root.classList.add('is-paused');
  };
  const play = () => {
    clearTimeout(timer);
    root.classList.remove('is-paused');
    if (reduced.matches || document.hidden) return;
    timer = setTimeout(() => {
      show(index + 1);
      play();
    }, delay);
  };
  dots.forEach((dot, i) => dot.addEventListener('click', () => { show(i); play(); }));
  root.addEventListener('focusin', stop);
  root.addEventListener('focusout', play);
  document.addEventListener('visibilitychange', () => (document.hidden ? stop() : play()));
  show(0);
  play();
};

/* Tabs (chips) */
const initTabs = (root) => {
  if (!once(root, 'clReady')) return;
  const tabs = [...root.querySelectorAll('[role="tab"]')];
  const select = (tab, focus) => {
    tabs.forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
      const panel = root.querySelector(`#${t.getAttribute('aria-controls')}`);
      if (panel) panel.hidden = !on;
    });
    if (focus) tab.focus();
  };
  tabs.forEach((tab, i) => {
    tab.addEventListener('click', () => select(tab));
    tab.addEventListener('keydown', (event) => {
      const forward = isRtl(root) ? 'ArrowLeft' : 'ArrowRight';
      const back = isRtl(root) ? 'ArrowRight' : 'ArrowLeft';
      if (event.key === forward) select(tabs[(i + 1) % tabs.length], true);
      if (event.key === back) select(tabs[(i - 1 + tabs.length) % tabs.length], true);
    });
  });
};

/* Rails with previous and next buttons */
const initRail = (root) => {
  if (!once(root, 'clReady')) return;
  const rail = root.querySelector('.cl-rail');
  const [prev, next] = root.querySelectorAll('.cl-rail-nav button');
  if (!rail || !prev || !next) return;
  const update = () => {
    const max = rail.scrollWidth - rail.clientWidth - 2;
    const pos = Math.abs(rail.scrollLeft);
    prev.disabled = pos <= 2;
    next.disabled = pos >= max;
  };
  const step = (dir) => {
    const amount = rail.clientWidth * 0.9 * dir * (isRtl(rail) ? -1 : 1);
    rail.scrollBy({ left: amount, behavior: reduced.matches ? 'auto' : 'smooth' });
  };
  prev.addEventListener('click', () => step(-1));
  next.addEventListener('click', () => step(1));
  rail.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update, { passive: true });
  update();
};

/* Collection filters: any change reloads the page with the new filter URL. */
const initFilters = (root) => {
  if (!once(root, 'clReady')) return;
  const form = root.querySelector('form[data-cl-filter-form]');
  const panel = root.querySelector('[data-cl-filter-panel]');
  const openers = root.querySelectorAll('[data-cl-filter-open]');
  const closers = root.querySelectorAll('[data-cl-filter-close]');
  const go = () => {
    const params = new URLSearchParams();
    for (const [key, value] of new FormData(form)) {
      if (String(value).trim() !== '') params.append(key, value);
    }
    root.classList.add('is-loading');
    window.location.search = params.toString();
  };
  let debounce;
  form?.addEventListener('change', (event) => {
    clearTimeout(debounce);
    debounce = setTimeout(go, event.target.type === 'number' ? 700 : 0);
  });
  form?.addEventListener('submit', (event) => {
    event.preventDefault();
    go();
  });
  const setOpen = (open) => {
    root.classList.toggle('is-filter-open', open);
    document.documentElement.style.overflow = open ? 'hidden' : '';
    openers.forEach((b) => b.setAttribute('aria-expanded', open ? 'true' : 'false'));
    if (open) panel?.querySelector('summary, input, button')?.focus();
  };
  openers.forEach((b) => b.addEventListener('click', () => setOpen(!root.classList.contains('is-filter-open'))));
  closers.forEach((b) => b.addEventListener('click', () => setOpen(false)));
  root.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && root.classList.contains('is-filter-open')) setOpen(false);
  });
};

/* Product: variant picker, gallery, add to cart, sticky bar */
const money = (cents, format) => {
  const value = (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (!format) return value;
  // Some currency formats wrap the amount in <span class=money>; the price is written as text, so drop tags.
  return format.replace(/<[^>]*>/g, '').trim().replace(/\{\{\s*(\w+)\s*\}\}/, (_, kind) => {
    if (kind === 'amount_no_decimals') return Math.round(cents / 100).toLocaleString('en-US');
    if (kind === 'amount_with_comma_separator') return value.replace(/,/g, '.').replace(/\.(\d{2})$/, ',$1');
    return value;
  });
};

const initProduct = (root) => {
  if (!once(root, 'clReady')) return;
  const data = JSON.parse(root.querySelector('[data-cl-variants]')?.textContent || '[]');
  const format = root.dataset.moneyFormat;
  const form = root.querySelector('form[data-cl-product-form]');
  const idInput = form?.querySelector('input[name="id"]');
  const fieldsets = [...root.querySelectorAll('[data-cl-option]')];
  const priceEl = root.querySelectorAll('[data-cl-price]');
  const compareEl = root.querySelectorAll('[data-cl-compare]');
  const saveEl = root.querySelector('[data-cl-save]');
  const button = root.querySelector('[data-cl-add]');
  const buttonLabel = button?.querySelector('span');
  const engraving = root.querySelector('[data-cl-engraving]');
  const gallery = root.querySelector('[data-cl-gallery]');
  const counter = root.querySelector('[data-cl-counter]');
  const status = root.querySelector('[data-cl-status]');
  const labels = { add: root.dataset.labelAdd, soldOut: root.dataset.labelSoldOut, unavailable: root.dataset.labelUnavailable, added: root.dataset.labelAdded };

  const engraveInput = engraving?.querySelector('input[type="text"]');
  const engravePrice = engraving?.querySelector('[data-cl-engrave-price]');
  const engraveCount = engraving?.querySelector('[data-cl-engrave-count]');
  const engravePreview = engraving?.querySelector('[data-cl-engrave-preview]');
  const engravingIndex = engraving ? Number(engraving.dataset.optionIndex) : -1;
  const engravingOn = (values) => engravingIndex >= 0 && !!values[engravingIndex] && values[engravingIndex] !== engraving.dataset.noneValue;

  const selected = () => fieldsets.map((fs) => fs.querySelector('input:checked')?.value);
  const findVariant = (values) => data.find((v) => v.options.every((o, i) => o === values[i]));

  const markAvailability = (values) => {
    fieldsets.forEach((fs, position) => {
      fs.querySelectorAll('input').forEach((input) => {
        const test = [...values];
        test[position] = input.value;
        const match = findVariant(test);
        input.closest('label')?.classList.toggle('is-unavailable', !match || !match.available);
      });
      const legendValue = fs.querySelector('[data-cl-option-value]');
      if (legendValue) legendValue.textContent = values[position] || '';
    });
  };

  const showMedia = (mediaId) => {
    if (!gallery || !mediaId) return;
    const target = gallery.querySelector(`[data-media-id="${mediaId}"]`);
    if (!target) return;
    if (window.matchMedia('(max-width: 989px)').matches) {
      gallery.scrollTo({ left: target.offsetLeft - gallery.offsetLeft, behavior: reduced.matches ? 'auto' : 'smooth' });
    } else {
      gallery.prepend(target);
    }
  };

  const update = (push) => {
    const values = selected();
    const variant = findVariant(values);
    markAvailability(values);
    if (engraving) {
      const on = engravingOn(values);
      engraving.classList.toggle('is-on', on);
      if (engraveInput) engraveInput.required = on;
      if (engravePrice) {
        // Show what the engraving adds: the engraved variant's price minus the same watch without it.
        const plain = [...values];
        const marked = [...values];
        plain[engravingIndex] = engraving.dataset.noneValue;
        marked[engravingIndex] = engraving.dataset.onValue;
        const a = findVariant(plain);
        const b = findVariant(marked);
        const extra = a && b ? b.price - a.price : 0;
        engravePrice.textContent = extra > 0 ? `+${money(extra, format)}` : '';
      }
    }
    if (!variant) {
      if (button) button.disabled = true;
      if (buttonLabel) buttonLabel.textContent = labels.unavailable;
      return;
    }
    if (idInput) idInput.value = variant.id;
    priceEl.forEach((el) => (el.textContent = money(variant.price, format)));
    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    compareEl.forEach((el) => {
      el.hidden = !onSale;
      if (onSale) el.textContent = money(variant.compare_at_price, format);
    });
    if (saveEl) {
      saveEl.hidden = !onSale;
      if (onSale) saveEl.textContent = `-${Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100)}%`;
    }
    if (button) button.disabled = !variant.available;
    if (buttonLabel) buttonLabel.textContent = variant.available ? labels.add : labels.soldOut;
    if (variant.featured_media) showMedia(variant.featured_media.id);
    if (push) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  };

  fieldsets.forEach((fs) => fs.addEventListener('change', () => update(true)));

  // Name box: typing a name picks the engraved option, clearing it goes back to no engraving,
  // and the drawn caseback shows the name as it is typed.
  const pickEngraving = (value) => {
    const radio = [...(fieldsets[engravingIndex]?.querySelectorAll('input') || [])].find((input) => input.value === value);
    if (radio && !radio.checked) {
      radio.checked = true;
      update(true);
    }
  };
  const drawName = () => {
    if (!engraveInput) return;
    const text = engraveInput.value.trim();
    if (engraveCount) engraveCount.textContent = `${engraveInput.value.length}/${engraveInput.maxLength}`;
    if (engravePreview) {
      engravePreview.textContent = text || engravePreview.dataset.placeholder || '';
      engravePreview.classList.toggle('is-empty', !text);
      const size = text.length > 14 ? 9 : text.length > 9 ? 12 : 16;
      engravePreview.style.fontSize = `${size}px`;
    }
    engraving.classList.toggle('has-name', !!text);
  };
  if (engraving && engraveInput) {
    engraveInput.addEventListener('input', () => {
      pickEngraving(engraveInput.value.trim() ? engraving.dataset.onValue : engraving.dataset.noneValue);
      drawName();
    });
    fieldsets[engravingIndex]?.addEventListener('change', () => {
      if (engravingOn(selected())) {
        if (!engraveInput.value.trim()) engraveInput.focus({ preventScroll: true });
      } else if (engraveInput.value) {
        engraveInput.value = '';
        drawName();
      }
    });
    drawName();
  }
  update(false);

  if (gallery && counter) {
    const items = [...gallery.children];
    gallery.addEventListener(
      'scroll',
      () => {
        const width = gallery.clientWidth || 1;
        const i = Math.round(Math.abs(gallery.scrollLeft) / width);
        counter.textContent = `${Math.min(i + 1, items.length)} / ${items.length}`;
      },
      { passive: true }
    );
  }

  form?.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (!button || button.disabled) return;
    if (!form.reportValidity()) return;
    button.disabled = true;
    root.classList.add('is-adding');
    const formData = new FormData(form);
    if (engraveInput && !engraveInput.value.trim()) formData.delete(engraveInput.name);
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')].map((el) => el.dataset.sectionId);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        if (status) status.textContent = result.description || result.message || '';
        return;
      }
      if (status) status.textContent = '';
      if (buttonLabel) buttonLabel.textContent = labels.added;
      const drawer = document.querySelector('cart-drawer-component');
      try {
        const { CartAddEvent } = await import('@theme/events');
        document.dispatchEvent(
          new CartAddEvent({}, String(formData.get('id')), {
            source: 'product-form-component',
            itemCount: Number(formData.get('quantity')) || 1,
            productId: root.dataset.productId,
            sections: result.sections,
          })
        );
      } catch (error) {
        console.warn('CrownLab: theme events unavailable', error);
      }
      if (drawer) {
        if (!drawer.hasAttribute('auto-open') && typeof drawer.open === 'function') drawer.open();
      } else {
        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      }
      setTimeout(() => {
        if (buttonLabel) buttonLabel.textContent = labels.add;
      }, 2000);
    } catch (error) {
      console.error(error);
      form.submit();
    } finally {
      button.disabled = false;
      root.classList.remove('is-adding');
    }
  });

  const sticky = root.querySelector('[data-cl-sticky]');
  if (sticky && button && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(([entry]) => {
      const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sticky.classList.toggle('is-visible', past);
    });
    observer.observe(button);
    sticky.querySelector('button')?.addEventListener('click', () => form?.requestSubmit());
  }
};

/* Product recommendations, rendered by Shopify and fetched after load */
const initRecos = async (root) => {
  if (!once(root, 'clReady')) return;
  try {
    const response = await fetch(root.dataset.url);
    const html = await response.text();
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-cl-recos]');
    if (fresh && fresh.querySelector('.cl-card')) {
      root.innerHTML = fresh.innerHTML;
      root.hidden = false;
      root.querySelectorAll('[data-cl-rail]').forEach(initRail);
      initReveal(root);
      initTilt(root);
    } else {
      root.remove();
    }
  } catch (error) {
    root.remove();
  }
};

/* 3D motion. Everything here is decoration: it is skipped for reduced motion,
   and the pointer effects only run on devices with a real mouse. */
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const wide = window.matchMedia('(min-width: 990px)');

// Sections rise into place with a little depth as they scroll into view.
const revealSelector = '.cl-head, .cl-card, .cl-tile, .cl-look, .cl-family, .cl-story__media, .cl-story__copy, .cl-trust li, .cl-accordion, .cl-clist__item, .cl-news__inner, .cl-promises li, .cl-steps li, .cl-info__card, .cl-point, .cl-page-text__grid, .cl-page-contact__grid > *, .cl-page-cta__inner, .cl-product__about > .cl-h2, .cl-product__desc > p, .cl-product__desc li';
const initReveal = (scope) => {
  if (reduced.matches || !('IntersectionObserver' in window)) return;
  const io = new IntersectionObserver(
    (entries) => entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-in');
      io.unobserve(entry.target);
    }),
    { rootMargin: '0px 0px -6% 0px', threshold: 0.04 }
  );
  scope.querySelectorAll(revealSelector).forEach((el) => {
    if (el.closest('.cl-hero, .cl-filters, .cl-zoom, .cl-explode') || !once(el, 'clReveal')) return;
    const index = Array.prototype.indexOf.call(el.parentElement.children, el);
    el.style.setProperty('--cl-delay', `${Math.min(index, 5) * 70}ms`);
    io.observe(el);
  });
};

// Cards and tiles lean toward the pointer, with a soft light that follows it.
const initTilt = (scope) => {
  if (reduced.matches || !finePointer.matches) return;
  scope.querySelectorAll('.cl-card__media, .cl-tile, .cl-family__media, .cl-photo-tile, .cl-clist__media, .cl-info__media, .cl-point').forEach((el) => {
    if (!once(el, 'clTilt')) return;
    el.classList.add('cl-tilt');
    const strength = el.matches('.cl-card__media, .cl-family__media') ? 10 : 6;
    let frame;
    el.addEventListener('pointermove', (event) => {
      const box = el.getBoundingClientRect();
      const x = (event.clientX - box.left) / box.width - 0.5;
      const y = (event.clientY - box.top) / box.height - 0.5;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        el.style.setProperty('--cl-rx', `${(-y * strength).toFixed(2)}deg`);
        el.style.setProperty('--cl-ry', `${(x * strength * 1.2).toFixed(2)}deg`);
        el.style.setProperty('--cl-gx', `${((x + 0.5) * 100).toFixed(1)}%`);
        el.style.setProperty('--cl-gy', `${((y + 0.5) * 100).toFixed(1)}%`);
      });
    });
    el.addEventListener('pointerleave', () => {
      cancelAnimationFrame(frame);
      el.style.setProperty('--cl-rx', '0deg');
      el.style.setProperty('--cl-ry', '0deg');
    });
  });
};

// The hero photo sits deeper than the text: it drifts against the pointer and sinks slower on scroll.
const initDepth = (root) => {
  if (reduced.matches || !once(root, 'clDepth')) return;
  const slides = root.querySelector('.cl-hero__slides');
  const copy = root.querySelector('.cl-hero__copy');
  if (!slides) return;
  let px = 0;
  let py = 0;
  let sy = 0;
  let frame = null;
  const apply = () => {
    frame = null;
    if (!wide.matches) {
      slides.style.transform = '';
      if (copy) copy.style.transform = '';
      return;
    }
    slides.style.transform = `translate3d(${(px * -18).toFixed(1)}px, ${(py * -12 + sy * 0.28).toFixed(1)}px, 0) scale(${(1.05 + Math.min(sy, 800) / 8000).toFixed(4)})`;
    if (copy) copy.style.transform = `translate3d(${(px * 12).toFixed(1)}px, ${(py * 8 - sy * 0.1).toFixed(1)}px, 0)`;
  };
  const queue = () => {
    if (!frame) frame = requestAnimationFrame(apply);
  };
  if (finePointer.matches) {
    root.addEventListener('pointermove', (event) => {
      const box = root.getBoundingClientRect();
      px = (event.clientX - box.left) / box.width - 0.5;
      py = (event.clientY - box.top) / box.height - 0.5;
      queue();
    });
  }
  window.addEventListener('scroll', () => {
    const box = root.getBoundingClientRect();
    if (box.bottom < 0) return;
    sy = Math.max(0, -box.top);
    queue();
  }, { passive: true });
  wide.addEventListener('change', queue);
  queue();
};

// The lookbook photos turn like a carousel of frames as the rail scrolls.
const initCoverflow = (rail) => {
  if (reduced.matches || !once(rail, 'clCover')) return;
  const items = [...rail.querySelectorAll('.cl-look__media')];
  if (!items.length) return;
  let frame = null;
  const apply = () => {
    frame = null;
    const box = rail.getBoundingClientRect();
    const center = box.left + box.width / 2;
    items.forEach((item) => {
      const r = item.getBoundingClientRect();
      const d = Math.max(-1.4, Math.min(1.4, (r.left + r.width / 2 - center) / box.width));
      item.style.transform = `perspective(1100px) rotateY(${(d * -16).toFixed(2)}deg) translateZ(${(-Math.abs(d) * 40).toFixed(1)}px)`;
    });
  };
  const queue = () => {
    if (!frame) frame = requestAnimationFrame(apply);
  };
  rail.addEventListener('scroll', queue, { passive: true });
  window.addEventListener('resize', queue);
  queue();
};

// Copy a discount code to the clipboard and say so on the button for a moment.
const initCopy = (button) => {
  if (!once(button, 'clCopy')) return;
  button.addEventListener('click', async () => {
    try {
      await navigator.clipboard.writeText(button.dataset.clCopy);
    } catch (error) {
      return;
    }
    const label = button.textContent;
    button.textContent = button.dataset.copied || 'Copied';
    setTimeout(() => { button.textContent = label; }, 1600);
  });
};

// The zoom photo tilts up from the page and grows to fill the screen while its track scrolls past.
const initZoom = (root) => {
  if (!once(root, 'clZoom')) return;
  const track = root.querySelector('.cl-zoom__track');
  const stage = root.querySelector('.cl-zoom__stage');
  if (!track || !stage) return;
  if (reduced.matches) {
    root.classList.add('cl-zoom--static');
    stage.style.setProperty('--p', '1');
    return;
  }
  let frame = null;
  const apply = () => {
    frame = null;
    const box = track.getBoundingClientRect();
    const run = box.height - window.innerHeight;
    const p = run > 0 ? Math.min(1, Math.max(0, -box.top / (run * 0.8))) : 1;
    stage.style.setProperty('--p', p.toFixed(4));
  };
  const queue = () => {
    if (!frame) frame = requestAnimationFrame(apply);
  };
  window.addEventListener('scroll', queue, { passive: true });
  window.addEventListener('resize', queue);
  queue();
};

// Exploded watch: the picture grows from a card until it fills the screen, then the watch opens into its parts
// and slowly pushes in as the track scrolls past.
const initExplode = (root) => {
  if (!once(root, 'clExplode')) return;
  const track = root.querySelector('.cl-explode__track');
  if (!track) return;
  if (reduced.matches) {
    root.classList.add('cl-explode--static', 'is-open');
    return;
  }
  const clamp = (v) => Math.min(1, Math.max(0, v));
  const ease = (t) => t * t * (3 - 2 * t);
  let frame = null;
  const apply = () => {
    frame = null;
    const box = track.getBoundingClientRect();
    const run = box.height - window.innerHeight;
    const p = run > 0 ? clamp(-box.top / (run * 0.92)) : 1;
    // grow while the section scrolls in and through the first third, then open, then a slow push-in
    const e = ease(clamp((window.innerHeight * 0.6 - box.top) / (window.innerHeight * 0.6 + run * 0.3)));
    const r = ease(clamp((p - 0.34) / 0.32));
    const z = clamp((p - 0.7) / 0.3);
    root.style.setProperty('--z', z.toFixed(4));
    root.style.setProperty('--p', p.toFixed(4));
    root.style.setProperty('--e', e.toFixed(4));
    root.style.setProperty('--r', r.toFixed(4));
  };
  const queue = () => {
    if (!frame) frame = requestAnimationFrame(apply);
  };
  window.addEventListener('scroll', queue, { passive: true });
  window.addEventListener('resize', queue);
  queue();
};

// Page headers: the photo sinks slower than the page and the title floats forward.
const initPageHero = (root) => {
  if (reduced.matches || !once(root, 'clPageHero')) return;
  const media = root.querySelector('[data-cl-page-media]');
  const copy = root.querySelector('[data-cl-page-copy]');
  if (!media) return;
  let frame = null;
  const apply = () => {
    frame = null;
    const y = Math.max(0, window.scrollY);
    if (y > window.innerHeight * 1.2) return;
    media.style.transform = `translate3d(0, ${(y * 0.32).toFixed(1)}px, 0) scale(${(1 + y / 4000).toFixed(4)})`;
    if (copy) {
      copy.style.transform = `perspective(900px) translate3d(0, ${(y * -0.08).toFixed(1)}px, ${(Math.min(y, 400) / 10).toFixed(1)}px)`;
      copy.style.opacity = String(Math.max(0, 1 - y / (window.innerHeight * 0.9)).toFixed(3));
    }
  };
  const queue = () => {
    if (!frame) frame = requestAnimationFrame(apply);
  };
  window.addEventListener('scroll', queue, { passive: true });
  queue();
};

// Watch tour: as you scroll, the product photo glides from one close-up to the next and the matching
// explanation opens. The zoom follows the scroll continuously; the last step turns the card over.
const initAnatomy = (root) => {
  if (!once(root, 'clReady')) return;
  const card = root.querySelector('[data-cl-anat-card]');
  const steps = [...root.querySelectorAll('.cl-anat__step')];
  const photos = [...root.querySelectorAll('.cl-anat__photo')];
  const bar = root.querySelector('.cl-anat__bar');
  if (!card || !steps.length) return;
  if (reduced.matches) { root.classList.add('cl-anat--static'); return; }
  const state = steps.map((step) => ({
    photo: step.dataset.photo || '1',
    zoom: (Number(step.dataset.zoom) || 10) / 10,
    x: Number(step.dataset.x ?? 50),
    y: Number(step.dataset.y ?? 45),
  }));
  const ease = (t) => t * t * (3 - 2 * t);
  const lerp = (a, b, t) => a + (b - a) * t;
  let active = -1;
  let shownPhoto = '1';
  let frame;
  const setActive = (index) => {
    if (index === active) return;
    active = index;
    steps.forEach((step, i) => step.classList.toggle('is-active', i === index));
  };
  const showPhoto = (photo) => {
    if (photo === 'back' || photo === shownPhoto) return;
    shownPhoto = photo;
    photos.forEach((el) => el.classList.toggle('is-shown', el.dataset.photo === photo));
  };
  const update = () => {
    frame = null;
    const box = root.getBoundingClientRect();
    const run = Math.max(1, box.height - window.innerHeight);
    const progress = Math.min(1, Math.max(0, -box.top / run));
    bar?.style.setProperty('--progress', progress.toFixed(3));
    const f = progress * (steps.length - 1);
    const i = Math.min(steps.length - 2, Math.floor(f));
    const t = steps.length > 1 ? ease(Math.min(1, Math.max(0, f - Math.max(0, i)))) : 0;
    const a = state[Math.max(0, i)];
    const b = state[Math.min(steps.length - 1, Math.max(0, i) + 1)];
    setActive(Math.round(f));
    // Zoom glides within the same photo; a photo change crossfades at the midpoint.
    const from = a.photo === 'back' ? b : a;
    const to = b.photo === 'back' ? a : b;
    const same = from.photo === to.photo;
    const zt = same ? t : (t < 0.5 ? 0 : 1);
    const zoom = lerp(from.zoom, to.zoom, zt);
    const x = lerp(from.x, to.x, zt);
    const y = lerp(from.y, to.y, zt);
    showPhoto(t < 0.5 ? from.photo : to.photo);
    photos.forEach((el) => {
      const img = el.querySelector('img');
      if (!img) return;
      img.style.setProperty('--zoom', zoom.toFixed(3));
      img.style.setProperty('--x', `${x}%`);
      img.style.setProperty('--y', `${y}%`);
    });
    const flipA = a.photo === 'back' ? 1 : 0;
    const flipB = b.photo === 'back' ? 1 : 0;
    const flip = lerp(flipA, flipB, t);
    card.style.setProperty('--flip', flip.toFixed(3));
    card.style.setProperty('--lift', Math.sin(flip * Math.PI).toFixed(3));
  };
  const request = () => { if (!frame) frame = requestAnimationFrame(update); };
  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('resize', request);
  steps.forEach((step, index) => step.querySelector('.cl-anat__num')?.addEventListener('click', () => {
    const box = root.getBoundingClientRect();
    const run = box.height - window.innerHeight;
    const target = steps.length > 1 ? index / (steps.length - 1) : 0;
    window.scrollTo({ top: window.scrollY + box.top + run * target, behavior: 'smooth' });
  }));
  update();
};

// Description: bold-only lines become small headings, long points take the full row.
const initDesc = (root) => {
  if (!once(root, 'clDesc')) return;
  root.querySelectorAll('p').forEach((p) => {
    const strong = p.querySelector('strong, b');
    if (strong && !p.closest('li') && strong.textContent.trim() === p.textContent.trim()) p.classList.add('cl-desc-label');
  });
  root.querySelectorAll('li').forEach((li) => {
    if (li.textContent.trim().length > 90 || li.querySelectorAll('p, div, ul').length > 1) li.classList.add('is-wide');
  });
};

// Stock counters ("Only 10 left") that apps add next to the buy box are hidden.
const initStockFree = (root) => {
  if (!once(root, 'clStockFree')) return;
  const scope = root.closest('.shopify-section') || root;
  const pattern = /(hurry|left in stock|units? left|items? left|only \d+ left|נותרו רק|بقي فقط)/i;
  let frame;
  const sweep = () => {
    frame = null;
    scope.querySelectorAll('div, p, span').forEach((el) => {
      if (el.style.display === 'none' || /\bcl-/.test(el.className || '') || el.closest('.cl-product__more, .cl-accordion')) return;
      const text = el.textContent.trim();
      if (!text || text.length > 160 || !pattern.test(text)) return;
      let target = el;
      while (target.parentElement && target.parentElement !== scope && !/\bcl-/.test(target.parentElement.className || '') && target.parentElement.textContent.trim() === text) target = target.parentElement;
      target.style.setProperty('display', 'none', 'important');
    });
  };
  sweep();
  new MutationObserver(() => { if (!frame) frame = requestAnimationFrame(sweep); }).observe(scope, { childList: true, subtree: true });
};

const init = (scope = document) => {
  scope.querySelectorAll('[data-cl-hero]').forEach(initHero);
  scope.querySelectorAll('[data-cl-tabs]').forEach(initTabs);
  scope.querySelectorAll('[data-cl-rail]').forEach(initRail);
  scope.querySelectorAll('[data-cl-filters]').forEach(initFilters);
  scope.querySelectorAll('[data-cl-product]').forEach(initProduct);
  scope.querySelectorAll('[data-cl-product]').forEach(initStockFree);
  scope.querySelectorAll('.cl-product__desc').forEach(initDesc);
  scope.querySelectorAll('[data-cl-anat]').forEach(initAnatomy);
  scope.querySelectorAll('[data-cl-recos]').forEach(initRecos);
  scope.querySelectorAll('[data-cl-hero]').forEach(initDepth);
  scope.querySelectorAll('.cl-lookbook__rail').forEach(initCoverflow);
  scope.querySelectorAll('[data-cl-copy]').forEach(initCopy);
  scope.querySelectorAll('[data-cl-zoom]').forEach(initZoom);
  scope.querySelectorAll('[data-cl-explode]').forEach(initExplode);
  scope.querySelectorAll('[data-cl-page]').forEach(initPageHero);
  initReveal(scope);
  initTilt(scope);
};

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
else init();
document.addEventListener('shopify:section:load', (event) => init(event.target));
